const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+";
const CHAR_GROUPS = [LOWER, UPPER, DIGITS, SYMBOLS];

/** Length of a generated password — comfortably above the account-management policy's 10-character minimum. */
export const GENERATED_PASSWORD_LENGTH = 16;

/** Draws a cryptographically random index in `[0, exclusiveMax)`. */
function randomIndex(exclusiveMax: number): number {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] % exclusiveMax;
}

function randomChar(chars: string): string {
    return chars[randomIndex(chars.length)];
}

/** Fisher-Yates shuffle using {@link randomIndex} (not `Math.random`), so the guaranteed-group
 * characters end up at unpredictable positions instead of always leading the string. */
function shuffle<T>(items: T[]): T[] {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

/**
 * #19351: Generates a random {@link GENERATED_PASSWORD_LENGTH}-character password containing all four
 * character groups (lower/upper case, digit, symbol) the account-management password policy requires —
 * for the "Starkes Passwort generieren" button in {@link PasswordDialog}. Uses `crypto.getRandomValues`,
 * never `Math.random`.
 */
export function generateStrongPassword(): string {
    // One guaranteed character from each required group, the rest filled from the combined pool.
    const required = CHAR_GROUPS.map(randomChar);
    const allChars = CHAR_GROUPS.join("");
    const rest = Array.from({length: GENERATED_PASSWORD_LENGTH - required.length}, () => randomChar(allChars));
    return shuffle([...required, ...rest]).join("");
}
